import { useCallback } from 'react';
import { omitBy } from 'lodash';
import { useRouter } from 'next/router';

import { StringParam, useQueryParams, withDefault } from '@blockworks/platform/libs/use-query-params';

import { shareFeedbackModalParams } from '../modal-types/share-feedback/share-feedback-modal.model';
import type { QueryModalTypes } from '../models';

const removeQueryModalParamsFromUrl = (url: string) => {
    const [path, query = ''] = url.split('?');

    const params = Object.fromEntries(new URLSearchParams(query));
    const filteredObj = omitBy(params, (_, key) => key.startsWith('modal'));
    const filteredQuery = new URLSearchParams(filteredObj).toString();

    if (filteredQuery) {
        return `${path}?${filteredQuery}`;
    }
    return path ?? url;
};

const modalParamsConfig = {
    modal: withDefault(StringParam, ''),
    ...shareFeedbackModalParams,
};

type ExtraParams = Partial<Omit<Record<keyof typeof modalParamsConfig, string>, 'modal'>>;

const useQueryModal = () => {
    const { asPath } = useRouter();
    const [{ modal, ...modalParams }, setParams] = useQueryParams(modalParamsConfig, { removeDefaultsFromUrl: true });

    const getRouteWithoutModalParams = useCallback(() => removeQueryModalParamsFromUrl(asPath), [asPath]);

    const openModal = useCallback(
        (modalType: QueryModalTypes, extraParams: ExtraParams = {}) => {
            setParams({ modal: modalType, ...extraParams });
        },
        [setParams],
    );

    const closeModal = useCallback(() => {
        setParams({ modal: '', modalCategory: '' });
    }, [setParams]);

    return {
        currentModal: modal as QueryModalTypes,
        modalParams: modalParams,
        openModal,
        closeModal,
        getRouteWithoutModalParams,
    };
};

export { useQueryModal };
